import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import useDataLayer from '@/utils/useDataLayer';

import {matrixSelector} from '../../../slices/matrixSlice';
import {appSelector, dpleConfigSelector} from '../../../slices/appSlice';
import {pushSnapshotQuote} from '../../../dple/utils/dppEndpoints';

/**
 * Right-rail CTA for DPLE template-led mode (ONE-10006).
 *
 * Mirrors the QAU Upload journey's cart hand-off (see Dispatch/Dpo/Upload.jsx
 * `dpoFinish` → its hidden-form POST to /dpo/product/directAddToCart): we
 * send the right-rail's current `print_options`, `qty`, `product`,
 * `webproduct`, `current_selection_id` plus the editor metadata, and Magento
 * builds the cart line from them. The artwork-side fields are the only thing
 * that change — instead of the QAU's downloadable `qauPdf` URL +
 * `artworkId`, we send `snapshot_id` (the DPLE projectSnapshotId), which
 * Avery's async asset pipeline (Avery\AsyncAssets\Model\Asset\Pdf) reads
 * out of the serialized editor data and uses to fetch the real artwork
 * PDF from DPP after the order is placed.
 *
 * `previewUrl` from exitToOrder doubles as `qauPreview` so the cart line's
 * preview thumbnail is populated immediately without waiting for the async
 * pipeline.
 *
 * The QAU multi-step modal (Dispatch/Dpo/Upload.jsx + Dispatch/Dpo/index.jsx)
 * stays reachable for every other configurator page; this component is
 * mounted from index.jsx behind dpleEnabled.
 */
const DpleCta = ({trackingRef}) => {
  const {
    baseUrl,
    code,
    productId,
    webproductId,
    currentOptions,
    productTags,
    prices,
    dpoQuote,
    isFetching,
  } = useSelector(matrixSelector);
  const {dpoSettings} = useSelector(appSelector);
  const dpleConfig = useSelector(dpleConfigSelector);
  const [submitting, setSubmitting] = useState(false);
  const [pushEvent] = useDataLayer();

  // The button stays disabled until we have a selectable price — submitting
  // without a quantity would land in M2 with `qty=` and silently create a
  // wrong cart line.
  const selectedQty = prices?.values?.find((p) => p.is_selected)?.quantity;
  const disabled =
    submitting ||
    isFetching ||
    !selectedQty ||
    currentOptions.find((opt) => opt.is_out_of_stock === true) !== undefined;

  const handleClick = async () => {
    if (disabled) return;

    const sdk = window.lightEditorSDK;
    if (!sdk?.exitToOrder) {
      // eslint-disable-next-line no-console
      console.error('[DPLE] lightEditorSDK is not available; cannot finalise design.');
      return;
    }

    setSubmitting(true);
    let form = null;
    try {
      const result = await sdk.exitToOrder();
      if (!result?.success || !result.projectSnapshotId || !result.sku) {
        // eslint-disable-next-line no-console
        console.error('[DPLE] exitToOrder failed', result?.error || result);
        setSubmitting(false);
        return;
      }
      // M2's DirectAddToCart unconditionally requires `qauPreview` and
      // throws "Cannot create print assets" on an empty value, so refuse
      // to submit without it. Per SDK docs `previewUrl` is optional —
      // surfacing a clear console error beats a generic Magento error page.
      if (!result.previewUrl) {
        // eslint-disable-next-line no-console
        console.error('[DPLE] exitToOrder returned no previewUrl; aborting cart hand-off.');
        setSubmitting(false);
        return;
      }

      pushEvent({
        event: 'triggerEvent',
        eventCategory: 'DPO',
        eventAction: 'Configurator-DPLE',
        eventLabel: 'DPLE-FinishToCart',
      });
      trackingRef?.current?.trackStartDesigningCta?.('DPLE');
      trackingRef?.current?.trackFinishConfigurator?.();

      // Options changed in the order step update the price matrix but not
      // the DPP project — push the current quote onto the fresh snapshot so
      // the press files are generated from the ordered configuration.
      // Failure-tolerant: the helper logs any skip or failure and returns,
      // and the cart hand-off below proceeds either way. Awaited on purpose
      // so the update reaches DPP before Magento can act on the snapshot.
      await pushSnapshotQuote({
        dpoSettings,
        quote: dpoQuote,
        exitResult: result,
        consumer: dpleConfig?.consumer,
        fallbackDeploymentId: dpleConfig?.target,
      });

      const currentSelectionId = `prod_${productId}_${Date.now()}_${Math.floor(
        Math.random() * 100,
      )}`;

      // Same field set the QAU Upload journey assembles in dpoFinish() — the
      // deltas are the editor identity (snapshot_id replaces qauPdf +
      // artworkId), the source of the preview URL, and the is_dple flag below.
      const formElements = {
        sku: result.sku,
        snapshot_id: result.projectSnapshotId,
        qauPreview: result.previewUrl,
        labelHeight: dpoQuote?.labelHeight ?? '',
        labelWidth: dpoQuote?.labelWidth ?? '',
        product_tags: productTags,
        product: productId,
        webproduct: webproductId,
        qty: selectedQty,
        current_selection_id: currentSelectionId,
        // Positively marks this as a light-editor order for Magento's order
        // tagging. Without it, Magento infers "light editor" from a missing
        // DPO product SKU, which also matches ordinary canvas orders that
        // reach the cart without one and mislabels them. The flag rides in
        // the serialized editor data, so it also survives a reorder.
        is_dple: '1',
        'print_options[PP][]': code,
      };

      for (const opt of currentOptions) {
        const key = opt.is_product_part
          ? `print_options[PP_${code}][${opt.id}][]`
          : `print_options[${opt.id}][${opt.id}][]`;
        formElements[key] = opt.value;
      }

      form = document.createElement('form');
      form.method = 'POST';
      form.action = `${baseUrl}/dpo/product/directAddToCart`;
      form.style.display = 'none';
      for (const [name, value] of Object.entries(formElements)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value ?? '';
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
      // The browser keeps the POST body alive across the navigation, so the
      // form node itself is no longer needed. Removing it prevents an orphan
      // <form> from accumulating in the DOM if the navigation is cancelled
      // (unload handler, network failure) — without that the user would also
      // be stuck on a disabled button until they reload.
      form.remove();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[DPLE] exitToOrder threw', error);
      if (form) form.remove();
      setSubmitting(false);
    }
  };

  return (
    // aria-live region announces the transition from "Add to basket" to
    // "Sending to cart…" to screen reader users — without it, the label
    // change is silent.
    <ft-button
      class="button-configurator-completed"
      onClick={handleClick}
      variant="1"
      full
      size="big"
      aria-live="polite"
      aria-busy={submitting ? 'true' : undefined}
      {...(disabled ? {disabled: true} : {})}
    >
      {submitting ? Drupal.t('Sending to cart…') : Drupal.t('Add to basket')}
    </ft-button>
  );
};

export default DpleCta;
